import { useState } from 'react';
import { ButtonLink, Checkbox, Toggle } from '@resideo/blueprint-oneportal';
import styles from './InviteEmployeeStepLocation.module.css';
import { CameraAccessModal } from './CameraAccessModal';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface LocationDevice {
  id: string;
  name: string | null;
}

export interface LocationRow {
  id: string;
  name: string;
  fullAddress: string;
  devices: LocationDevice[];              // cameras
  thermostatDevices?: LocationDevice[];
  leakDetectorDevices?: LocationDevice[];
  lockDevices?: LocationDevice[];         // placeholder
  securityPanelDevices?: LocationDevice[]; // placeholder
}

export interface StepLocationProps {
  locations: LocationRow[];
  isLoading: boolean;
  error: string | null;
  selectedIds: Set<string>;
  cameraSelections: Map<string, Set<string>>;
  camerasOn: Set<string>;
  onToggleLocation: (id: string) => void;
  onCameraSelectionsChange: (next: Map<string, Set<string>>) => void;
  onCamerasOnChange: (next: Set<string>) => void;
  readOnly?: boolean;
  emptyMessage?: string;
}

// ─── Device category config ───────────────────────────────────────────────────

const DEVICE_CATEGORIES = [
  { key: 'securityPanels', label: 'Security Panel',  enabled: false },
  { key: 'cameras',        label: 'Cameras',         enabled: true  },
  { key: 'locks',          label: 'Locks',           enabled: false },
  { key: 'thermostats',    label: 'Thermostats',     enabled: false },
  { key: 'leakDetectors',  label: 'Leak Detectors',  enabled: false },
] as const;

// ─── Icons ────────────────────────────────────────────────────────────────────

function SecurityPanelIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path
        d="M9 2L3 5v4c0 3.31 2.55 6.41 6 7.16C12.45 15.41 15 12.31 15 9V5L9 2z"
        stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"
      />
    </svg>
  );
}

function CameraIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="25" height="22" viewBox="0 0 25 22" fill="none">
      <path d="M18.2083 9.34548C17.656 9.34548 17.2083 9.7932 17.2083 10.3455C17.2083 10.8978 17.656 11.3455 18.2083 11.3455V10.3455V9.34548ZM22.4457 10.3455L22.4462 9.34548H22.4457V10.3455ZM23.4887 12.0337L24.3831 12.4808L24.3832 12.4806L23.4887 12.0337ZM21.1157 16.7808L22.01 17.2282L22.0101 17.2279L21.1157 16.7808ZM19.123 16.9372L19.9366 16.3558L19.9364 16.3554L19.123 16.9372ZM17.4584 12.8904C17.1371 12.4412 16.5125 12.3375 16.0633 12.6588C15.6141 12.98 15.5103 13.6046 15.8316 14.0539L16.645 13.4722L17.4584 12.8904ZM18.6237 6.90732L19.0713 6.0131L19.0713 6.01308L18.6237 6.90732ZM19.1452 8.47182L20.0396 8.91909L20.0398 8.9186L19.1452 8.47182ZM15.5215 15.718L14.6271 15.2707L14.6271 15.2707L15.5215 15.718ZM13.9558 16.2395L14.4034 15.3452L14.4034 15.3452L13.9558 16.2395ZM2.87833 10.6955L2.42847 11.5886L2.43078 11.5897L2.87833 10.6955ZM1.36167 6.13382L2.25673 6.57976L2.25801 6.57717L1.36167 6.13382ZM2.97167 2.87882L2.07786 2.43032L2.07532 2.43546L2.97167 2.87882ZM7.545 1.36215L7.09632 2.25584L7.09741 2.25639L7.545 1.36215ZM1 17.5122H0V19.5122H1V18.5122V17.5122ZM5.38667 18.5122L5.39363 17.5122H5.38667V18.5122ZM7.48667 17.2288L8.37972 17.6788L8.38233 17.6736L7.48667 17.2288ZM10.0623 14.2902L10.5071 13.3946L8.71575 12.5051L8.27101 13.4007L9.16667 13.8455L10.0623 14.2902ZM0 20.8455V21.8455H2V20.8455H1H0ZM2 16.1788V15.1788H0V16.1788H1H2ZM6.83333 5.84548C6.28105 5.84548 5.83333 6.2932 5.83333 6.84548C5.83333 7.39777 6.28105 7.84548 6.83333 7.84548V6.84548V5.84548ZM6.845 7.84548C7.39728 7.84548 7.845 7.39777 7.845 6.84548C7.845 6.2932 7.39728 5.84548 6.845 5.84548V6.84548V7.84548ZM18.2083 10.3455V11.3455H22.4457V10.3455V9.34548H18.2083V10.3455ZM22.4457 10.3455L22.4451 11.3455C22.4735 11.3455 22.5015 11.3528 22.5263 11.3666L23.0135 10.4934L23.5008 9.6201C23.1784 9.44021 22.8154 9.34568 22.4462 9.34548L22.4457 10.3455ZM23.0135 10.4934L22.5263 11.3666C22.5511 11.3804 22.5719 11.4004 22.5868 11.4246L23.4376 10.8989L24.2883 10.3733C24.0942 10.0593 23.8232 9.79998 23.5008 9.6201L23.0135 10.4934ZM23.4376 10.8989L22.5868 11.4246C22.6018 11.4487 22.6103 11.4763 22.6115 11.5047L23.6105 11.4597L24.6095 11.4147C24.5929 11.0459 24.4823 10.6874 24.2883 10.3733L23.4376 10.8989ZM23.6105 11.4597L22.6115 11.5047C22.6128 11.533 22.6068 11.5612 22.5941 11.5866L23.4887 12.0337L24.3832 12.4806C24.5482 12.1504 24.6261 11.7835 24.6095 11.4147L23.6105 11.4597ZM23.4887 12.0337L22.5942 11.5865L20.2212 16.3337L21.1157 16.7808L22.0101 17.2279L24.3831 12.4808L23.4887 12.0337ZM21.1157 16.7808L20.2213 16.3335C20.2085 16.3592 20.1892 16.3811 20.1653 16.3971L20.7239 17.2266L21.2824 18.0561C21.5923 17.8474 21.8429 17.5623 22.01 17.2282L21.1157 16.7808ZM20.7239 17.2266L20.1653 16.3971C20.1415 16.4132 20.1139 16.4228 20.0853 16.4251L20.1635 17.422L20.2417 18.4189C20.6141 18.3897 20.9726 18.2647 21.2824 18.0561L20.7239 17.2266ZM20.1635 17.422L20.0853 16.4251C20.0566 16.4273 20.0279 16.4221 20.0019 16.41L19.5795 17.3164L19.1571 18.2228C19.4957 18.3806 19.8693 18.4481 20.2417 18.4189L20.1635 17.422ZM19.5795 17.3164L20.0019 16.41C19.9758 16.3978 19.9533 16.3792 19.9366 16.3558L19.123 16.9372L18.3094 17.5185C18.5265 17.8224 18.8186 18.0651 19.1571 18.2228L19.5795 17.3164ZM19.123 16.9372L19.9364 16.3554L17.4584 12.8904L16.645 13.4722L15.8316 14.0539L18.3096 17.5189L19.123 16.9372ZM18.6237 6.90732L18.176 7.80153C18.2155 7.8213 18.2456 7.85595 18.2595 7.89786L19.2082 7.58163L20.1569 7.26541C19.9753 6.7206 19.5848 6.27017 19.0713 6.0131L18.6237 6.90732ZM19.2082 7.58163L18.2595 7.89786C18.2735 7.93977 18.2703 7.98551 18.2505 8.02503L19.1452 8.47182L20.0398 8.9186C20.2964 8.40483 20.3385 7.81021 20.1569 7.26541L19.2082 7.58163ZM19.1452 8.47182L18.2508 8.02455L14.6271 15.2707L15.5215 15.718L16.4159 16.1653L20.0396 8.91909L19.1452 8.47182ZM15.5215 15.718L14.6271 15.2707C14.6173 15.2903 14.6037 15.3078 14.5872 15.3221L15.2423 16.0777L15.8974 16.8332C16.1124 16.6468 16.2886 16.4198 16.4159 16.1652L15.5215 15.718ZM15.2423 16.0777L14.5872 15.3221C14.5707 15.3365 14.5515 15.3474 14.5307 15.3543L14.8467 16.3031L15.1627 17.2518C15.4327 17.1619 15.6824 17.0196 15.8974 16.8332L15.2423 16.0777ZM14.8467 16.3031L14.5307 15.3543C14.5099 15.3612 14.488 15.364 14.4661 15.3624L14.3949 16.3599L14.3237 17.3574C14.6076 17.3776 14.8927 17.3418 15.1627 17.2518L14.8467 16.3031ZM14.3949 16.3599L14.4661 15.3624C14.4443 15.3609 14.423 15.355 14.4034 15.3452L13.9558 16.2395L13.5082 17.1337C13.7628 17.2611 14.0399 17.3371 14.3237 17.3574L14.3949 16.3599ZM13.9558 16.2395L14.4034 15.3452L3.32589 9.80123L2.87833 10.6955L2.43078 11.5897L13.5083 17.1337L13.9558 16.2395ZM2.87833 10.6955L3.32819 9.80238C2.75977 9.51607 2.3279 9.01624 2.1271 8.41229L1.17817 8.72779L0.229247 9.04329C0.596923 10.1491 1.38768 11.0643 2.42848 11.5886L2.87833 10.6955ZM1.17817 8.72779L2.1271 8.41229C1.9263 7.80834 1.97291 7.14943 2.25673 6.57976L1.36167 6.13382L0.466604 5.68788C-0.0530845 6.73096 -0.138428 7.93743 0.229247 9.04329L1.17817 8.72779ZM1.36167 6.13382L2.25801 6.57717L3.86801 3.32217L2.97167 2.87882L2.07532 2.43546L0.46532 5.69046L1.36167 6.13382ZM2.97167 2.87882L3.86546 3.3273C4.00722 3.04479 4.20325 2.79296 4.44234 2.58621L3.78826 1.82979L3.13418 1.07337C2.69638 1.45193 2.33745 1.91303 2.07788 2.43033L2.97167 2.87882ZM3.78826 1.82979L4.44234 2.58621C4.68144 2.37946 4.95893 2.22184 5.25895 2.12234L4.94417 1.17317L4.6294 0.224007C4.08005 0.406189 3.57197 0.694804 3.13418 1.07337L3.78826 1.82979ZM4.94417 1.17317L5.25895 2.12234C5.55897 2.02284 5.87566 1.98342 6.19092 2.00633L6.26338 1.00896L6.33585 0.0115868C5.75861 -0.030356 5.17875 0.0418244 4.6294 0.224007L4.94417 1.17317ZM6.26338 1.00896L6.19092 2.00633C6.50618 2.02924 6.81383 2.11402 7.09632 2.25584L7.545 1.36215L7.99368 0.468456C7.47643 0.208775 6.9131 0.0535299 6.33585 0.0115868L6.26338 1.00896ZM7.545 1.36215L7.09741 2.25639L18.1761 7.80156L18.6237 6.90732L19.0713 6.01308L7.99259 0.467911L7.545 1.36215ZM1 18.5122V19.5122H5.38667V18.5122V17.5122H1V18.5122ZM5.38667 18.5122L5.37971 19.5121C6.00092 19.5165 6.61099 19.3471 7.14107 19.0231L6.61962 18.1699L6.09817 17.3166C5.88614 17.4462 5.64211 17.5139 5.39363 17.5122L5.38667 18.5122ZM6.61962 18.1699L7.14107 19.0231C7.67116 18.6992 8.10017 18.2336 8.37971 17.6788L7.48667 17.2288L6.59363 16.7788C6.48181 17.0008 6.31021 17.187 6.09817 17.3166L6.61962 18.1699ZM7.48667 17.2288L8.38233 17.6736L10.0623 14.2902L9.16667 13.8455L8.27101 13.4007L6.59101 16.7841L7.48667 17.2288ZM1 20.8455H2V16.1788H1H0V20.8455H1ZM6.83333 6.84548V7.84548H6.845V6.84548V5.84548H6.83333V6.84548Z" fill="#605B51" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="26" viewBox="0 0 24 26" fill="none">
  <path d="M19.8327 11.771V22.166H3.49935V11.771H19.8327ZM11.666 1.16602C7.87435 1.16602 4.78268 4.22268 4.78268 7.99102V9.44935H1.16602V24.511H22.166V9.44935H18.5377V7.99102C18.5377 6.18268 17.826 4.46768 16.5193 3.17268C15.2127 1.88935 13.486 1.17768 11.666 1.17768V1.16602ZM7.11602 9.43768V7.97935C7.11602 5.49435 9.15768 3.47602 11.666 3.47602C12.8677 3.47602 14.011 3.94268 14.8743 4.79435C15.7377 5.65768 16.2043 6.78935 16.2043 7.97935V9.43768H7.11602Z" fill="#605B51"/>
</svg>
  );
}

function ThermostatIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="23" height="19" viewBox="0 0 23 19" fill="none">
      <path d="M0.681641 2.88481C7.68563 9.41398 14.6896 -3.64436 21.6816 2.88481Z" fill="#DBE1F0" />
      <path d="M0.681641 9.16666C7.68563 15.6958 14.6896 2.63749 21.6816 9.16666Z" fill="#DBE1F0" />
      <path d="M0.681641 15.4485C7.6976 21.9777 14.6776 8.91935 21.6816 15.4485Z" fill="#DBE1F0" />
      <path d="M0.681641 2.88481C7.68563 9.41398 14.6896 -3.64436 21.6816 2.88481M0.681641 9.16666C7.68563 15.6958 14.6896 2.63749 21.6816 9.16666M0.681641 15.4485C7.6976 21.9777 14.6776 8.91935 21.6816 15.4485" stroke="#605B51" strokeWidth="2" strokeMiterlimit="10" />
    </svg>
  );
}

function LeakIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path
        d="M9 2C9 2 3.5 8.5 3.5 12a5.5 5.5 0 0011 0C14.5 8.5 9 2 9 2z"
        stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"
      />
    </svg>
  );
}

// ─── StepLocation component ───────────────────────────────────────────────────

export function StepLocation({
  locations,
  isLoading,
  error,
  selectedIds,
  cameraSelections,
  camerasOn,
  onToggleLocation,
  onCameraSelectionsChange,
  onCamerasOnChange,
  readOnly = false,
  emptyMessage = 'No locations found for this account.',
}: StepLocationProps) {
  const [searchQuery, setSearchQuery] = useState('');
  // Modal state
  const [modalState, setModalState] = useState<{
    locationId: string;
    locationName: string;
    devices: { id: string; name: string | null }[];
  } | null>(null);

  // ─── Derived values + refs/effects — MUST be before any early returns ──────
  const filteredLocations = searchQuery.trim()
    ? locations.filter(l =>
        l.name.toLowerCase().includes(searchQuery.trim().toLowerCase())
      )
    : locations;

  const allSelected =
    filteredLocations.length > 0 &&
    filteredLocations.every(l => selectedIds.has(l.id));
  const someSelected =
    !allSelected && filteredLocations.some(l => selectedIds.has(l.id));

  function handleSelectAll() {
    if (allSelected) {
      // Deselect all: remove every visible location and clear their camera state
      const nextSel = new Map(cameraSelections);
      const nextOn = new Set(camerasOn);
      filteredLocations.forEach(l => {
        if (selectedIds.has(l.id)) {
          onToggleLocation(l.id);
          nextSel.set(l.id, new Set());
          nextOn.delete(l.id);
        }
      });
      onCameraSelectionsChange(nextSel);
      onCamerasOnChange(nextOn);
    } else {
      // Select all: add every unselected location and initialise their camera state
      const nextSel = new Map(cameraSelections);
      const nextOn = new Set(camerasOn);
      filteredLocations.forEach(l => {
        if (!selectedIds.has(l.id)) {
          onToggleLocation(l.id);
          nextSel.set(l.id, new Set(l.devices.map(d => d.id)));
          nextOn.add(l.id);
        }
      });
      onCameraSelectionsChange(nextSel);
      onCamerasOnChange(nextOn);
    }
  }

  function toggleCameras(locationId: string) {
    const turningOff = camerasOn.has(locationId);
    const nextCamerasOn = new Set(camerasOn);
    if (turningOff) nextCamerasOn.delete(locationId); else nextCamerasOn.add(locationId);
    onCamerasOnChange(nextCamerasOn);
    const nextSelections = new Map(cameraSelections);
    if (turningOff) {
      nextSelections.set(locationId, new Set());
    } else {
      const loc = locations.find(l => l.id === locationId);
      nextSelections.set(locationId, new Set(loc?.devices.map(d => d.id) ?? []));
    }
    onCameraSelectionsChange(nextSelections);
  }

  // ─── Loading state ──────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className={styles.stateContainer}>
        {[1, 2, 3].map(i => (
          <div key={i} className={styles.loadingSkeleton} />
        ))}
      </div>
    );
  }

  // ─── Error state ────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className={styles.stateContainer}>
        <span className={styles.errorText}>{error}</span>
      </div>
    );
  }

  // ─── Empty state ────────────────────────────────────────────────────────────
  if (locations.length === 0) {
    return (
      <div className={styles.stateContainer}>{emptyMessage}</div>
    );
  }

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className={styles.locationList}>
      {/* Title + subtitle */}
      <h2 className={styles.sectionTitle}>Select Locations</h2>
      <p className={styles.sectionSubtitle}>Assign site and device access.</p>

      {/* Search bar */}
      <div className={styles.searchBar}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className={styles.searchIcon}>
          <circle cx="6.5" cy="6.5" r="4.5" stroke="currentColor" strokeWidth="1.4" />
          <path d="M10 10l3.5 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
        <input
          type="text"
          className={styles.searchInput}
          placeholder="Search"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Name column header + location list — bordered container */}
      <div className={styles.listBorder}>
      <div className={styles.nameHeader}>
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected}
          onChange={handleSelectAll}
          disabled={readOnly}
        />
        <span className={styles.nameHeaderLabel}>Name</span>
      </div>

      <div className={styles.locationItemsScroll}>
      {filteredLocations.map(loc => {
        const isSelected = selectedIds.has(loc.id);
        const cameraTotal = loc.devices.length;
        const cameraSelectedCount = (cameraSelections.get(loc.id) ?? new Set()).size;
        const camerasEnabled = camerasOn.has(loc.id);

        return (
          <div key={loc.id} className={styles.locationItem}>

            {/* ── Location header row ─────────────────────────────────────── */}
            <div className={styles.locationRow}>
              {/* Checkbox */}
              <Checkbox
                checked={isSelected}
                onChange={() => {
                  if (readOnly) return;
                  const selecting = !isSelected;
                  onToggleLocation(loc.id);
                  if (selecting) {
                    const nextSel = new Map(cameraSelections);
                    nextSel.set(loc.id, new Set(loc.devices.map(d => d.id)));
                    onCameraSelectionsChange(nextSel);
                    onCamerasOnChange(new Set([...camerasOn, loc.id]));
                  } else {
                    const nextSel = new Map(cameraSelections);
                    nextSel.set(loc.id, new Set());
                    onCameraSelectionsChange(nextSel);
                    const nextOn = new Set(camerasOn);
                    nextOn.delete(loc.id);
                    onCamerasOnChange(nextOn);
                  }
                }}
                disabled={readOnly}
              />

              {/* Name + address */}
              <div className={styles.locationInfo}>
                <span className={styles.locationName}>{loc.name}</span>
                {loc.fullAddress && (
                  <span className={styles.locationAddress}>{loc.fullAddress}</span>
                )}
              </div>
            </div>

            {/* ── Device category rows ─────────────────────────────────── */}
            <div className={styles.deviceRows}>
                {DEVICE_CATEGORIES.map(cat => (
                  <div
                    key={cat.key}
                    className={[
                      styles.devicesBox,
                      !cat.enabled ? styles.deviceTypeRowDisabled : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                  <div className={styles.deviceTypeRow}>
                    {/* Left: icon + label + count */}
                    <div className={styles.deviceTypeLeft}>
                      <span className={styles.deviceTypeIcon}>
                        {cat.key === 'securityPanels' && <SecurityPanelIcon />}
                        {cat.key === 'cameras'        && <CameraIcon />}
                        {cat.key === 'locks'          && <LockIcon />}
                        {cat.key === 'thermostats'    && <ThermostatIcon />}
                        {cat.key === 'leakDetectors'  && <LeakIcon />}
                      </span>
                      <span className={styles.deviceTypeName}>{cat.label}</span>
                      {cat.key === 'cameras' && (
                        <span className={styles.deviceCount}>
                          {cameraSelectedCount} of {cameraTotal} cameras
                        </span>
                      )}
                      {cat.key === 'thermostats' && (loc.thermostatDevices ?? []).length > 0 && (
                        <span className={styles.deviceCount}>
                          {(loc.thermostatDevices ?? []).length} of {(loc.thermostatDevices ?? []).length} thermostats
                        </span>
                      )}
                      {cat.key === 'leakDetectors' && (loc.leakDetectorDevices ?? []).length > 0 && (
                        <span className={styles.deviceCount}>
                          {(loc.leakDetectorDevices ?? []).length} of {(loc.leakDetectorDevices ?? []).length} leak detectors
                        </span>
                      )}
                    </div>

                    {/* Right: Edit Access / View Access link + toggle */}
                    <div className={styles.deviceTypeRight}>
                      {cat.key === 'cameras' && !readOnly && (
                        <ButtonLink
                          className={styles.editAccessLink}
                          onClick={() =>
                            setModalState({
                              locationId: loc.id,
                              locationName: loc.name,
                              devices: loc.devices,
                            })
                          }
                        >
                          Edit Access
                        </ButtonLink>
                      )}
                      {cat.key === 'cameras' && readOnly && cameraTotal > 0 && (
                        <ButtonLink
                          className={styles.editAccessLink}
                          onClick={() =>
                            setModalState({
                              locationId: loc.id,
                              locationName: loc.name,
                              devices: loc.devices,
                            })
                          }
                        >
                          View Access
                        </ButtonLink>
                      )}
                      <span className={styles.toggleWrapper}>
                      <Toggle
                        name={`cameras-${loc.id}`}
                        size="large"
                        checked={cat.enabled ? camerasEnabled : false}
                        onChange={() => { if (cat.enabled && isSelected && !readOnly) toggleCameras(loc.id); }}
                        disabled={!cat.enabled || !isSelected || readOnly}
                      />
                      </span>
                    </div>
                  </div>
                  </div>
                ))}
              </div>
          </div>
        );
      })}
      </div>
      </div>

      {/* Camera Access Modal */}
      {modalState && (
        <CameraAccessModal
          locationName={modalState.locationName}
          devices={modalState.devices}
          initialSelected={cameraSelections.get(modalState.locationId) ?? new Set()}
          onSave={readOnly ? undefined : selected => {
            const next = new Map(cameraSelections);
            next.set(modalState.locationId, selected);
            onCameraSelectionsChange(next);
            setModalState(null);
          }}
          onCancel={() => setModalState(null)}
          readOnly={readOnly}
        />
      )}
    </div>
  );
}
